import { createContext, useContext, useState, useMemo, useEffect, useCallback } from "react";
import { $getRoot } from "lexical";
import { $generateHtmlFromNodes } from "@lexical/html";
import { KEY_ENTER_COMMAND, COMMAND_PRIORITY_HIGH } from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

import { BASE_URL } from "@/react/pages/qc/base/{utils}/constants";
import ToasterWrapper from '@/react/components/ToasterWrapper';
const { toast } = ToasterWrapper

const EditorContext = createContext(null);

export const useEditorContext = () => useContext(EditorContext);

const parseFile = (file, type) => {
	return {
		file,
		type,
		name: file.name,
		uuid: crypto.randomUUID() + Date.now().toString(),
		isPending: true,
		isFailed: false,
		url: null,
	}
}

const onFileUpload = (payload) => {
	const url = `${BASE_URL}api/upload-comment-attachments/temp`
	return axios.post(
		url,
		payload, {
		headers: HEADER_OBJ
	});
}

const onFileRemove = (payload) => {
	const url = `${BASE_URL}api/upload-comment-attachments/temp`
	return axios.delete(url, {
		params: payload,
		headers: HEADER_OBJ
	});
}


export const EditorProvider = ({ children, onSubmit, commentEdit = false, onCancel, clearSubmission = true, placeholder, focus = false }) => {
	const [attachments, setAttachments] = useState([]);
	const [showTopToolbar, setShowTopToolbar] = useState(false);
	const [sendDisabled, setSendDisabled] = useState(true);
	const [editor] = useLexicalComposerContext();

	const validateFile = useCallback((file) => {
		if (attachments.length + 1 >= 10) {
			toast.error("Maximum 10 attachments allowed.");
			return false;
		}

		const maxSize = 20 * 1024 * 1024; // 20MB
		if (file.size > maxSize) {
			toast.error("File size exceeds 20MB limit.");
			return false;
		}

		// disallowed types
		const forbiddenExtensions = [
			".exe", ".bat", ".cmd", ".sh", ".js", ".msi", ".php", ".py", ".pl", ".rb", ".jar", ".com", ".vbs", ".wsf", ".ps1"
		];


		if (forbiddenExtensions.some(ext => file.name.toLowerCase().endsWith(ext))) {
			toast.error("Unsupported file type.");
			return false;
		}

		return true;
	}, [attachments])

	const addAttachment = async (file) => {
		if (!validateFile(file)) return;
		const type = file.type.startsWith("image/") ? "image" : "file";
		const newFile = parseFile(file, type);
		const formData = new FormData();
		formData.append('file', newFile.file);
		formData.append('name', newFile.name);
		formData.append('uuid', newFile.uuid);
		formData.append('type', newFile.type);
		setAttachments(prev => [...prev, newFile]);

		try {
			const response = (await onFileUpload(formData)).data;
			const fileUrl = response.data.url; // Adjust based on your API response structure
			// mark as uploaded
			setAttachments(prev => prev.map(file => file.uuid === newFile.uuid ? { ...file, isPending: false, url: fileUrl } : file));
		} catch (error) {
			// mark as not uploading
			setAttachments(prev => prev.map(file => file.uuid === newFile.uuid ? { ...file, isFailed: true, isPending: false } : file));
			console.error("File upload failed:", error);
		}
	}

	const removeAttachment = async (uuid) => {
		const removedAttachment = attachments.find(file => file.uuid === uuid);
		setAttachments(prev => prev.filter(file => file.uuid !== uuid));
		try {
			if (!removedAttachment) throw new Error("Attachment not found");
			if (removedAttachment.isPending) return; // no need to inform server if it was never uploaded
			await onFileRemove({ name: removedAttachment.name, uuid: removedAttachment.uuid })
		} catch (error) {
			console.error("File removal failed:", error);
		}
	}

	const retryAttachment = async (uuid) => {
		const attachment = attachments.find(file => file.uuid === uuid);
		if (!attachment) return;
		removeAttachment(uuid);
		addAttachment(attachment.file);
	}

	const submitEditor = useCallback(async () => {
		// check if any attachment is still uploading or failed 
		if (attachments.some(file => file.isPending || file.isFailed)) {
			toast.error("Please wait for all attachments to finish uploading or remove failed ones.");
			return;
		}

		let html = "";
		let text = "";
		let mentions = [];

		editor.read(() => {
			html = $generateHtmlFromNodes(editor); // ✅ inside read
			text = $getRoot().getTextContent();

			const collectMentions = (nodes) => {
				nodes.forEach((node) => {
					if (node.__type === "mention") {
						mentions.push({ id: node.__id });
					}
					if (typeof node.getChildren === "function") {
						collectMentions(node.getChildren());
					}
				});
			};
			collectMentions($getRoot().getChildren());
		});

		if (sendDisabled) {
			toast.error("Cannot submit empty comment");
			return
		}

		onSubmit({ html, text, mentions, attachments });

		if (!clearSubmission) return;

		setAttachments([]);
		editor.update(() => { $getRoot().clear(); });
	}, [editor, onSubmit, attachments, clearSubmission, sendDisabled]);

	const computeDisabled = () => {
		editor.getEditorState().read(() => {
			const text = $getRoot().getTextContent().trim();
			setSendDisabled(
				(text.length === 0 && attachments.length === 0) ||
				attachments.some(f => f.isPending || f.isFailed)
			);
		});
	};

	// typing updates
	useEffect(() => {
		return editor.registerUpdateListener(() => {
			computeDisabled();
		});
	}, [editor, attachments]);

	// attachment updates
	useEffect(() => {
		computeDisabled();
	}, [attachments]);

	useEffect(() => {
		return editor.registerCommand(
			KEY_ENTER_COMMAND,
			(event) => {
				// If Shift+Enter, allow submission

				if (!event.shiftKey || sendDisabled) {
					return false; // let Lexical handle it
				}

				event.preventDefault(); // avoid new line
				submitEditor();
				return true; // stop Lexical’s default behavior
			},
			COMMAND_PRIORITY_HIGH
		);
	}, [editor, submitEditor, sendDisabled]);

	const value = useMemo(() => ({
		attachments,
		showTopToolbar,
		setShowTopToolbar,
		sendDisabled,
		setSendDisabled,
		editor,
		submitEditor,
		addAttachment,
		removeAttachment,
		retryAttachment,
		placeholder,
		focus,
		commentEdit,
		onCancel,
	}), [attachments, showTopToolbar, sendDisabled, editor, onSubmit, submitEditor, placeholder, focus, commentEdit, onCancel]);

	return (
		<EditorContext.Provider value={value}>
			{children}
		</EditorContext.Provider>
	);
};
